import React, { useState, useEffect } from 'react';
import { collection, query, where, getDocs, doc, updateDoc, deleteDoc, deleteField } from 'firebase/firestore';
import { initializeFirebase } from '../firebase/config';
import { getAuth, deleteUser } from 'firebase/auth';
import AddOperatorModal from './AddOperatorModal';
import EditOperatorModal from './EditOperatorModal';
import { pesos } from './CampoLimiteMonto';
import { adminService } from '../services/adminService';
import ConfirmModal from './ConfirmModal';
import OperatorCreatedModal from './OperatorCreatedModal';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTrash, faPen } from '@fortawesome/free-solid-svg-icons';

// Lo que ve de verdad cada uno (como limiteDeMonto en functions): los administradores
// todo; un operador, su límite propio o, si no tiene, el de la cuenta.
const Transferencias = ({ operator, limiteCuenta }) => {
    if (operator.admin) return 'Todas';
    if (operator.limite_monto) return `Hasta ${pesos(operator.limite_monto)}`;
    if (!limiteCuenta) return 'Todas';
    return (
        <>
            Hasta {pesos(limiteCuenta)}
            <span className="operator-limite__origen">límite de la cuenta</span>
        </>
    );
};

const OperatorRow = ({ operator, onActionSelect, onEdit, limiteCuenta }) => {
    const [showActions, setShowActions] = useState(false);

    // Formatear la fecha
    const formatDate = (dateString) => {
        if (!dateString) return '';
        const date = new Date(dateString);
        return date.toLocaleDateString('es-AR');
    };

    return (
        <div className="operator-row">
            <div className="operator-info">
                <div className="operator-name">{operator.nombre || operator.fullName}</div>
                <div className="operator-email">{operator.email}</div>
            </div>
            <div className="operator-role">
                {operator.admin ? 'Administrador' : 'Operador'}
            </div>
            <div className="operator-limite">
                <Transferencias operator={operator} limiteCuenta={limiteCuenta} />
            </div>
            <div className="operator-date">
                {formatDate(operator.createdAt)}
            </div>
            <div className="operator-actions">
                <button
                    className="action-button tooltip"
                    onClick={() => onEdit(operator)}
                    data-tooltip="Editar"
                >
                    <FontAwesomeIcon icon={faPen} />
                </button>
                <button
                    className="action-button tooltip"
                    onClick={() => onActionSelect(operator, 'delete')}
                    data-tooltip="Eliminar"
                >
                    <FontAwesomeIcon icon={faTrash} />
                </button>
                {showActions && (
                    <div className="actions-menu">
                        <button onClick={() => onActionSelect(operator, 'makeAdmin')}>
                            Definir como administrador
                        </button>
                        <button onClick={() => onActionSelect(operator, 'delete')}>
                            Eliminar
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
};

// `verComo` y `operadoresFijos` son para la vista por usuario del superadmin: la
// lista llega armada del backend (las reglas de Firestore no le dejan leer ni
// escribir los operadores de otro), así que los cambios van por endpoints del
// superadmin y después `onCambios` recarga la lista. Eliminar es la baja de la
// barra del superadmin (`onBaja`).
// `operadoresDePrueba` es para /test-modal: la lista no sale de Firestore y los
// cambios quedan solo en pantalla.
// `limiteCuenta` es el límite de los operadores sin uno propio (mp_accounts, que el
// navegador no lee). Si no viene, se le pide al backend al abrir.
function OperatorsModal({ isOpen, onClose, currentUser, verComo, operadoresFijos, onCambios, onBaja, operadoresDePrueba, limiteCuenta }) {
    const [searchTerm, setSearchTerm] = useState('');
    const [operators, setOperators] = useState([]);
    const [showAddModal, setShowAddModal] = useState(false);
    const [loading, setLoading] = useState(true);
    const [successMessage, setSuccessMessage] = useState(null);
    const [confirmModal, setConfirmModal] = useState({ show: false, action: null, operator: null });
    const [error, setError] = useState(null);
    const [createdOperatorModal, setCreatedOperatorModal] = useState({ show: false, email: '' });
    const [editando, setEditando] = useState(null);
    const [limiteLeido, setLimiteLeido] = useState(null);
    const limiteDeLaCuenta = limiteCuenta !== undefined ? limiteCuenta : limiteLeido;

    const loadOperators = async () => {
        if (operadoresDePrueba) {
            setOperators(actuales => actuales.length ? actuales : operadoresDePrueba.map(o => ({ id: o.email, ...o })));
            setLoading(false);
            return;
        }

        if (operadoresFijos) {
            setOperators(operadoresFijos.map(o => ({
                id: o.email,
                role: o.admin ? 'Administrador' : 'Operador',
                ...o
            })));
            setLoading(false);
            return;
        }

        try {
            const { db } = await initializeFirebase();

            if (!currentUser) {
                console.error('No hay usuario autenticado');
                return;
            }

            const q = query(
                collection(db, 'usuarios'),
                where('email_administrador', '==', currentUser.email)
            );

            const querySnapshot = await getDocs(q);
            const operatorsData = querySnapshot.docs.map(doc => ({
                id: doc.id,
                email: doc.data().email,
                name: doc.data().nombre,
                role: doc.data().admin ? 'Administrador' : 'Operador',
                creationDate: doc.data().createdAt,
                ...doc.data()
            }));

            setOperators(operatorsData);
        } catch (error) {
            console.error('Error al cargar operadores:', error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (isOpen) {
            loadOperators();
        }
    }, [isOpen, operadoresFijos]);

    useEffect(() => {
        if (!isOpen || limiteCuenta !== undefined || operadoresDePrueba) return;
        let vigente = true;
        adminService.limiteDeLaCuenta()
            .then(limite => { if (vigente) setLimiteLeido(limite); })
            .catch(error => console.error('No se pudo leer el límite de la cuenta:', error.message));
        return () => { vigente = false; };
    }, [isOpen, limiteCuenta, operadoresDePrueba]);

    if (!isOpen) return null;

    const filteredOperators = operators.filter(operator =>
        (operator.nombre || operator.fullName || operator.name)?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        operator.email?.toLowerCase().includes(searchTerm.toLowerCase())
    );

    // Como superadmin, a nombre del admin de otra cuenta (ver arriba). En la página de
    // prueba los cambios quedan en pantalla igual que siempre.
    const comoSuperadmin = !!verComo && !operadoresDePrueba;

    const handleActionSelect = async (operator, action) => {
        if (action === 'delete' && comoSuperadmin) {
            onBaja(operator.email);
        } else if (action === 'delete') {
            setConfirmModal({
                show: true,
                action: 'delete',
                operator,
                message: `¿Estás seguro que deseas eliminar al operador ${operator.nombre}?`
            });
        } else if (action === 'makeAdmin') {
            setConfirmModal({
                show: true,
                action: 'makeAdmin',
                operator,
                message: `¿Estás seguro que deseas convertir a ${operator.nombre} en administrador?`
            });
        }
    };

    const handleConfirmAction = async () => {
        const { action, operator } = confirmModal;
        setError(null);

        try {
            if (operadoresDePrueba) {
                setOperators(actuales => actuales.filter(o => o.id !== operator.id));
                setSuccessMessage('Operador eliminado exitosamente');
            } else {
                const { db } = await initializeFirebase();
                const operatorRef = doc(db, 'usuarios', operator.email);

                if (action === 'delete') {
                    await deleteDoc(operatorRef);
                    setSuccessMessage('Operador eliminado exitosamente');
                } else if (action === 'makeAdmin') {
                    await updateDoc(operatorRef, { admin: true });
                    setSuccessMessage('Operador actualizado a administrador exitosamente');
                }

                await loadOperators();
            }

            setTimeout(() => {
                setSuccessMessage(null);
            }, 4000);

        } catch (error) {
            console.error('Error:', error);
            setError(error.message);
            setTimeout(() => {
                setError(null);
            }, 4000);
        } finally {
            setConfirmModal({ show: false, action: null, operator: null });
        }
    };

    const handleAddOperatorSuccess = (message, operatorEmail, operadorDePrueba) => {
        // Mostrar el mensaje de éxito en la parte superior
        setSuccessMessage(message);

        // Mostrar el modal de confirmación con el email del operador
        setCreatedOperatorModal({
            show: true,
            email: operatorEmail
        });

        // Recargar la lista de operadores
        if (operadoresDePrueba) {
            setOperators(actuales => [...actuales, { id: operatorEmail, ...operadorDePrueba }]);
        } else if (comoSuperadmin) {
            onCambios();
        } else {
            loadOperators();
        }

        // Ocultar el mensaje de éxito después de un tiempo
        setTimeout(() => {
            setSuccessMessage(null);
        }, 4000);
    };

    // Rol y monto máximo de transferencias (EditOperatorModal). Sin límite se borra
    // el campo: el operador pasa a ver todas, o las del límite de la cuenta si tiene.
    const handleSaveOperator = async (operator, { admin, limite_monto }) => {
        if (operadoresDePrueba) {
            setOperators(actuales => actuales.map(o => (o.id === operator.id ? { ...o, admin, limite_monto } : o)));
        } else if (comoSuperadmin) {
            await adminService.editarOperador(operator.email, { admin, limite_monto });
            await onCambios();
        } else {
            const { db } = await initializeFirebase();
            await updateDoc(doc(db, 'usuarios', operator.email), {
                admin,
                limite_monto: limite_monto ?? deleteField()
            });
            await loadOperators();
        }

        setSuccessMessage(`Se guardaron los cambios de ${operator.nombre || operator.fullName || operator.email}`);
        setTimeout(() => {
            setSuccessMessage(null);
        }, 4000);
    };

    return (
        <>
            <div className="modal-overlay" onClick={(e) => {
                if (e.target === e.currentTarget) onClose();
            }}>
                <div className="modal-content">
                    <div className="modal-header">
                        <h2>Gestión de Operadores</h2>
                        <button className="close-button" onClick={onClose}>×</button>
                    </div>

                    {verComo && (
                        <div className="operators-ver-como">
                            Estás viendo la cuenta de <strong>{verComo}</strong> como administrador general:
                            lo que cambies acá se guarda en su cuenta.
                        </div>
                    )}

                    {successMessage && (
                        <div className={`success-message ${successMessage ? '' : 'fade-out'}`}>
                            {successMessage}
                        </div>
                    )}

                    {error && (
                        <div className={`error-message ${error ? '' : 'fade-out'}`}>
                            {error}
                        </div>
                    )}

                    <div className="modal-actions">
                        <div className="search-filter">
                            <input
                                type="text"
                                placeholder="Buscar colaborador"
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                            />
                        </div>
                        <button
                            className="invite-button"
                            onClick={() => setShowAddModal(true)}
                        >
                            <span className="button-text">Agregar operador</span>
                        </button>
                    </div>

                    <div className="operators-list">
                        <div className="operators-header">
                            <div>Colaborador</div>
                            <div>Rol</div>
                            <div>Transferencias</div>
                            <div>Fecha de creación</div>
                            <div></div>
                        </div>
                        {loading ? (
                            <div className="loading">Cargando operadores...</div>
                        ) : (
                            filteredOperators.map(operator => (
                                <OperatorRow
                                    key={operator.id}
                                    operator={operator}
                                    onActionSelect={handleActionSelect}
                                    onEdit={setEditando}
                                    limiteCuenta={limiteDeLaCuenta}
                                />
                            ))
                        )}
                    </div>
                </div>
            </div>

            <ConfirmModal
                isOpen={confirmModal.show}
                onClose={() => setConfirmModal({ show: false, action: null, operator: null })}
                onConfirm={handleConfirmAction}
                message={confirmModal.message}
            />

            <AddOperatorModal
                isOpen={showAddModal}
                onClose={() => setShowAddModal(false)}
                onSuccess={handleAddOperatorSuccess}
                currentUser={currentUser}
                verComo={comoSuperadmin ? verComo : undefined}
                limiteCuenta={limiteDeLaCuenta}
            />

            <EditOperatorModal
                operator={editando}
                onClose={() => setEditando(null)}
                onSave={handleSaveOperator}
                limiteCuenta={limiteDeLaCuenta}
            />

            <OperatorCreatedModal
                isOpen={createdOperatorModal.show}
                onClose={() => setCreatedOperatorModal({ show: false, email: '' })}
                operatorEmail={createdOperatorModal.email}
            />
        </>
    );
}

export default OperatorsModal; 